import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/public/PublicLayout';
import PageHero from '../../components/public/PageHero';
import { APPLY_ROUTE, withBase } from '../../publicSite/config';
import { trackEvent } from '../../publicSite/analytics';
import { usePageMeta } from '../../publicSite/usePageMeta';

const principles = [
  {
    title: 'Mission',
    copy: 'Equip believers with structured mastery that transforms families, economies, and communities.'
  },
  {
    title: 'Vision',
    copy: 'A generation of Kingdom builders who govern with wisdom, order, and impact.'
  },
  {
    title: 'Philosophy',
    copy: 'Spiritual alignment and practical systems working together to produce sustainable dominion.'
  }
];

const pillars = [
  {
    title: 'Foundation',
    copy: 'Faith, Kingdom principles, identity, purpose, and wisdom for generational growth.'
  },
  {
    title: 'Structure',
    copy: 'A clear curriculum, accountable cohorts, sound governance, and purposeful learning pathways.'
  },
  {
    title: 'Execution',
    copy: 'Practical assignments, coaching, feedback, and the discipline required to produce real outcomes.'
  }
];

const leaders = [
  { name: 'Dr Tich Tanyanyiwa', role: 'Chief Financial Officer', image: '/people/DR tich.webp' },
  { name: 'Dr Prinncisca Tanyanyiwa', role: 'Project Manager', image: '/people/Dr-Princisca.webp' },
  { name: 'Hannah', role: 'Operations', image: '/people/Hannah.webp' },
  { name: 'Jack Reddy', role: 'Systems Director', image: '/people/Reddy.webp' },
  { name: 'Deon Barnard', role: 'Creative Head', image: '/people/Deon.webp' },
  { name: 'Bheki Mlalazi', role: 'Executive Assistant', image: '/people/Becky.webp' },
  { name: 'Tandi Ndlovu', role: 'Leadership Team', image: '/people/Tandi.webp' }
];

export default function About() {
  usePageMeta(
    'About School of Shem',
    'Learn about the School of Shem ecosystem and how it equips families and leaders for Kingdom impact.'
  );

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Our institution"
        title="About School of SHEM"
        subtitle="A faith-aligned, excellence-driven learning ecosystem for people committed to building life with clarity, structure, and Kingdom purpose."
        image="/images/shem-academics-masterclass.jpg"
        imageAlt="Adult learners taking part in a School of SHEM masterclass"
      />

      <section className="ps-section">
        <div className="ps-section-inner public-split public-about-intro">
          <div className="public-prose">
            <div className="ps-hero-eyebrow">Who we are</div>
            <h2>Learning that moves from revelation to implementation.</h2>
          </div>
          <div className="public-prose">
            <p>
              School of SHEM combines structured learning, practical execution, and Kingdom alignment to strengthen
              faith, family, leadership, finances, and purpose.
            </p>
            <p>
              Its specialised faculties form one connected ecosystem, helping students build mastery in the domains
              that shape personal, family, and generational impact.
            </p>
          </div>
        </div>
      </section>

      <section className="public-image-statement" aria-label="School of SHEM learning experience">
        <img src="/images/shem-campus-hero.jpg" alt="A confident learner on a contemporary African campus" loading="lazy" />
        <div>
          <span>Built for whole-life mastery</span>
          <p>Where faith becomes framework, wisdom becomes action, and learning becomes a generational legacy.</p>
        </div>
      </section>

      <section className="ps-section ps-section-muted">
        <div className="ps-section-inner">
          <div className="public-principle-list">
            {principles.map((principle, index) => (
              <article key={principle.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h2>{principle.title}</h2>
                <p>{principle.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ps-section">
        <div className="ps-section-inner">
          <div className="ps-hero-eyebrow">Our approach</div>
          <h2 className="ps-section-title">Three pillars of formation</h2>
          <div className="public-number-grid public-number-grid-three">
            {pillars.map((pillar, index) => (
              <article className="public-number-item" key={pillar.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{pillar.title}</h3>
                <p>{pillar.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ps-section ps-section-muted">
        <div className="ps-section-inner">
          <div className="ps-section-heading-row">
            <div>
              <div className="ps-hero-eyebrow">The people serving the mission</div>
              <h2 className="ps-section-title">Leadership</h2>
            </div>
          </div>
          <div className="public-leadership-grid">
            {leaders.map((leader) => (
              <figure key={leader.name}>
                <img src={leader.image} alt={leader.name} loading="lazy" />
                <figcaption>
                  <strong>{leader.name}</strong>
                  <span>{leader.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="ps-section public-cta">
        <div className="ps-section-inner public-cta-inner">
          <div>
            <h2>Build with purpose.</h2>
            <p>Explore the School of SHEM academic pathways or begin your application.</p>
          </div>
          <div className="ps-hero-actions">
            <Link to={withBase('/academics/faculties')} className="btn btn-outline">Explore Academics</Link>
            <Link
              to={withBase(APPLY_ROUTE)}
              className="btn btn-primary"
              onClick={() => trackEvent('public_about_apply_click')}
            >
              Apply Now
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
